import { CSSObject, SerializedStyles, css } from '@emotion/react';
import useMediaQuery from 'hooks/useMediaQuery';

import { media } from './media';

export type ResponsiveValue<Atom> =
	| Readonly<[Atom, Atom, Atom, Atom, Atom, Atom]>
	| Readonly<[Atom, Atom, Atom]>
	| Readonly<[Atom, Atom]>
	| Atom;

type CSSResult = CSSObject | SerializedStyles;

function applyStyle<Key extends string | number>(
	value: Key,
	resolved: Record<Key, CSSResult> | ((value: Key) => CSSResult | undefined)
): CSSResult | undefined {
	if (typeof resolved === 'function') {
		return resolved(value);
	}
	return resolved[value];
}

export function getResponsiveStyles<Key extends string | number>(
	value: ResponsiveValue<Key> | undefined,
	valueMap: Record<Key, CSSResult> | ((value: Key) => CSSResult | undefined)
): SerializedStyles | undefined {
	if (value == null) return undefined;

	if (typeof value === 'string' || typeof value === 'number') {
		return css(applyStyle(value, valueMap));
	}

	if (Array.isArray(value)) {
		const { length } = value;

		if (length === 2) {
			const [mobile, tablet] = value;

			return css`
				@media ${media.lte('SM')} {
					${applyStyle(mobile, valueMap)}
				}

				@media ${media.gt('SM')} {
					${applyStyle(tablet, valueMap)}
				}
			`;
		}

		if (length === 3) {
			const [mobile, tablet, desktop] = value as Readonly<[Key, Key, Key]>;

			return css`
				@media ${media.lte('SM')} {
					${applyStyle(mobile, valueMap)}
				}

				@media ${media.gt('SM')} and ${media.lte('LG')} {
					${applyStyle(tablet, valueMap)}
				}

				@media ${media.gt('LG')} {
					${applyStyle(desktop, valueMap)}
				}
			`;
		}

		if (length === 6) {
			const [xs, sm, md, lg, xl, xxl] = value as Readonly<[Key, Key, Key, Key, Key, Key]>;

			return css`
				@media ${media.lte('XS')} {
					${applyStyle(xs, valueMap)}
				}

				@media ${media.gt('XS')} and ${media.lte('SM')} {
					${applyStyle(sm, valueMap)}
				}

				@media ${media.gt('SM')} and ${media.lte('MD')} {
					${applyStyle(md, valueMap)}
				}

				@media ${media.gt('MD')} and ${media.lte('LG')} {
					${applyStyle(lg, valueMap)}
				}

				@media ${media.gt('LG')} and ${media.lte('XL')} {
					${applyStyle(xl, valueMap)}
				}

				@media ${media.gt('XL')} {
					${applyStyle(xxl, valueMap)}
				}
			`;
		}

		if (length === 1) {
			const [mobile] = value;
			return css(applyStyle(mobile, valueMap));
		}

		throw new Error(`Invalid responsive value: ${JSON.stringify(value)}`);
	}

	throw new Error(`Invalid responsive value: ${JSON.stringify(value)}`);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useResponsiveVariant<Key extends string | number>(
	value: ResponsiveValue<Key>
): Key {
	const twoVariants = {
		mobile: useMediaQuery(media.lte('SM')),
	};

	const threeVariants = {
		mobile: twoVariants.mobile,
		tablet: useMediaQuery(`${media.gt('SM')} and ${media.lte('LG')}`),
		desktop: useMediaQuery(media.gt('LG')),
	};

	const sixVariants = {
		xs: useMediaQuery(media.lte('XS')),
		sm: useMediaQuery(`${media.gt('XS')} and ${media.lte('SM')}`),
		md: useMediaQuery(`${media.gt('SM')} and ${media.lte('MD')}`),
		lg: useMediaQuery(`${media.gt('MD')} and ${media.lte('LG')}`),
		xl: useMediaQuery(`${media.gt('LG')} and ${media.lte('XL')}`),
		xxl: useMediaQuery(media.gt('XL')),
	};

	if (typeof value === 'string' || typeof value === 'number') {
		return value;
	}

	if (Array.isArray(value)) {
		const { length } = value;

		if (length === 2) {
			const [mobile, tablet] = value;

			if (twoVariants.mobile) {
				return mobile;
			}

			return tablet;
		}

		if (length === 3) {
			const [mobile, tablet, desktop] = value as Readonly<[Key, Key, Key]>;

			if (threeVariants.mobile) {
				return mobile;
			}

			if (threeVariants.tablet) {
				return tablet;
			}

			return desktop;
		}

		if (length === 6) {
			const [xs, sm, md, lg, xl, xxl] = value as Readonly<[Key, Key, Key, Key, Key, Key]>;

			if (sixVariants.xs) {
				return xs;
			}

			if (sixVariants.sm) {
				return sm;
			}

			if (sixVariants.md) {
				return md;
			}

			if (sixVariants.lg) {
				return lg;
			}

			if (sixVariants.xl) {
				return xl;
			}

			return xxl;
		}

		if (length === 1) {
			const [mobile] = value;
			return mobile;
		}

		throw new Error(`Invalid responsive value: ${JSON.stringify(value)}`);
	}

	throw new Error(`Invalid responsive value: ${JSON.stringify(value)}`);
}
